import React, {
  useRef,
  useLayoutEffect,
  useState,
  useCallback,
} from 'react';
import TextField from '@material-ui/core/TextField';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import Popover from '@material-ui/core/Popover';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import FunctionsIcon from '@material-ui/icons/Functions';
import { LATEX_PALETTE } from './latexSymbolPalette';
import LatexMixedText from './LatexMixedText';
import { useLatexEditorForm } from './LatexEditorFormContext';

/**
 * Multiline-friendly TextField. LaTeX toolbar, symbol palette, and live preview
 * appear only when the parent form enables "Use LaTeX" (LatexEditorFormContext).
 */
const LatexTextField = ({
  value,
  onChange,
  containerStyle,
  showLatexTools = true,
  ...textFieldProps
}) => {
  const { useLatex } = useLatexEditorForm();
  const latexUiEnabled = showLatexTools && useLatex;

  const inputRef = useRef(null);
  const pendingSelectionRef = useRef(null);
  const [paletteAnchor, setPaletteAnchor] = useState(null);
  const [paletteTab, setPaletteTab] = useState(0);

  const insertAtSelection = useCallback(
    (snippet, cursorOffsetFromSnippetStart) => {
      const el = inputRef.current;
      if (!el || value == null) {
        return;
      }
      const str = String(value);
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newValue = str.slice(0, start) + snippet + str.slice(end);
      const rel =
        cursorOffsetFromSnippetStart != null
          ? cursorOffsetFromSnippetStart
          : snippet.length;
      pendingSelectionRef.current = start + rel;
      onChange({ target: { value: newValue } });
    },
    [onChange, value],
  );

  const wrapSelection = useCallback(
    (left, right) => {
      const el = inputRef.current;
      if (!el || value == null) {
        return;
      }
      const str = String(value);
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const selected = str.slice(start, end);
      const snippet = left + selected + right;
      const newValue = str.slice(0, start) + snippet + str.slice(end);
      pendingSelectionRef.current =
        start + left.length + selected.length + right.length;
      onChange({ target: { value: newValue } });
    },
    [onChange, value],
  );

  useLayoutEffect(() => {
    const el = inputRef.current;
    const pos = pendingSelectionRef.current;
    if (el != null && pos != null) {
      el.focus();
      el.setSelectionRange(pos, pos);
      pendingSelectionRef.current = null;
    }
  }, [value]);

  const openPalette = (e) => setPaletteAnchor(e.currentTarget);
  const closePalette = () => setPaletteAnchor(null);

  const currentCategory = LATEX_PALETTE[paletteTab];

  const previewSurfaceStyle = {
    marginTop: 8,
    padding: 12,
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
    minHeight: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  };

  if (!latexUiEnabled) {
    return (
      <div style={{ width: '100%', ...containerStyle }}>
        <TextField
          {...textFieldProps}
          value={value}
          onChange={onChange}
          inputRef={inputRef}
          fullWidth={textFieldProps.fullWidth !== false}
        />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', ...containerStyle }}>
      <Box
        display="flex"
        flexWrap="wrap"
        alignItems="center"
        justifyContent="flex-end"
        style={{ marginBottom: 4, gap: 4 }}
      >
        <Tooltip title="Wrap selection in inline math ($...$)">
          <Button
            size="small"
            variant="outlined"
            onClick={() => wrapSelection('$', '$')}
          >
            $…$
          </Button>
        </Tooltip>
        <Tooltip title="Wrap selection in display math ($$...$$)">
          <Button
            size="small"
            variant="outlined"
            onClick={() => wrapSelection('$$', '$$')}
          >
            $$…$$
          </Button>
        </Tooltip>
        <Tooltip title="Open symbol palette">
          <IconButton
            size="small"
            onClick={openPalette}
            aria-label="Open LaTeX symbol palette"
          >
            <FunctionsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={onChange}
        inputRef={inputRef}
        fullWidth={textFieldProps.fullWidth !== false}
      />
      <Box style={previewSurfaceStyle}>
        <Typography
          variant="caption"
          color="textSecondary"
          component="div"
          style={{ marginBottom: 6 }}
        >
          Live preview (same as in training)
        </Typography>
        {String(value || '').trim() === '' ? (
          <Typography variant="body2" color="textSecondary">
            Type text or use $…$ / $$…$$ for math.
          </Typography>
        ) : (
          <LatexMixedText text={value} block />
        )}
      </Box>
      <Popover
        open={Boolean(paletteAnchor)}
        anchorEl={paletteAnchor}
        onClose={closePalette}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box style={{ width: 320, maxHeight: 360, padding: 8 }}>
          <Typography variant="caption" color="textSecondary" component="p">
            Inline: $…$ · Display: $$…$$ · Palette inserts ready-to-use snippets.
          </Typography>
          <Tabs
            value={paletteTab}
            onChange={(_, v) => setPaletteTab(v)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {LATEX_PALETTE.map((cat) => (
              <Tab key={cat.id} label={cat.label} />
            ))}
          </Tabs>
          <Box
            display="flex"
            flexWrap="wrap"
            style={{ marginTop: 8, gap: 4 }}
          >
            {currentCategory.items.map((item) => (
              <Button
                key={`${currentCategory.id}_${item.label}`}
                size="small"
                variant="outlined"
                onClick={() => {
                  insertAtSelection(
                    item.insert,
                    item.cursorOffset != null ? item.cursorOffset : undefined,
                  );
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Popover>
    </div>
  );
};

export default LatexTextField;
